// ui/screen-shake.js: M5 Change #7.
//
// CSS-transform shake on the game root. Tiered to scoring magnitude
// (synthesis §3 Pillar 4 / Balatro SHOULD #9):
//   +1 majority      → no shake
//   +2 march/euchre  → ±4px translation, 300ms
//   +4 loner sweep   → ±8px translation + ±1° rotation, 500ms
//
// Reduced-motion: magnitude=0 → no-op. The screen-shake module never
// touches the OS media query directly; callers pass `motionAllowed`
// (mirrors easing.js's *OrReduced pattern).
//
// Implementation: ONE `transform: translateX/Y rotate` write per call,
// then a setTimeout to clear after `duration`. No per-frame JS, the
// transform animates via CSS transition (set by .screen-shake-target
// class in index.html / styles).

/** @typedef {'none'|'march'|'loner'} ShakeTier */

const TIER = Object.freeze({
  NONE:   { tx: 0, ty: 0, rot: 0, dur: 0   },
  MARCH:  { tx: 4, ty: 0, rot: 0, dur: 300 },
  LONER:  { tx: 8, ty: 0, rot: 1, dur: 500 },
});

/**
 * Map a Euchre scoring outcome to a tier.
 *
 * @param {{ delta?: number, loner?: boolean }} outcome
 * @returns {ShakeTier}
 */
export function tierFor(outcome) {
  if (!outcome) return 'none';
  if (outcome.loner) return 'loner';
  if ((outcome.delta || 0) >= 2) return 'march';
  return 'none';
}

/**
 * Apply a one-shot shake.
 *
 * @param {ShakeTier} tier
 * @param {{
 *   target?: HTMLElement|null,
 *   motionAllowed?: boolean,
 *   setTimeoutFn?: typeof setTimeout,
 * }} [opts]
 * @returns {boolean}  true if a shake was applied
 */
export function applyScreenShake(tier, opts = {}) {
  const motionAllowed = opts.motionAllowed !== false;
  const cfg = TIER[tier.toUpperCase()] || TIER.NONE;
  if (!motionAllowed || cfg.dur === 0) return false;
  const target = opts.target ?? (typeof document !== 'undefined' ? document.getElementById('app') : null);
  if (!target || !target.style) return false;
  const stFn = opts.setTimeoutFn ?? (typeof setTimeout !== 'undefined' ? setTimeout : null);
  // Apply once. The transition (defined on the target's stylesheet)
  // handles the return-to-identity over `dur` ms. We then clear the
  // inline transform so subsequent shakes start from identity.
  const tx = (Math.random() < 0.5 ? -1 : 1) * cfg.tx;
  const ty = cfg.ty === 0 ? 0 : (Math.random() < 0.5 ? -1 : 1) * cfg.ty;
  const rot = (Math.random() < 0.5 ? -1 : 1) * cfg.rot;
  target.style.transform = `translate(${tx}px, ${ty}px) rotate(${rot}deg)`;
  target.style.transition = `transform ${cfg.dur}ms ease-out`;
  if (stFn) {
    stFn(() => {
      target.style.transform = '';
      // Leave the transition class so the return animation plays cleanly.
      stFn(() => { target.style.transition = ''; }, cfg.dur);
    }, 16);
  }
  return true;
}

export const _internals = { TIER };
